import React from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, Collapse } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import CheckIcon from '@mui/icons-material/Check';

import {
  Card,
  CardHeader,
  CardTitleGroup,
  CardTitle,
  CardActions,
  SmallButtonGroup,
  CopyButton,
  DeleteButton,
  CollapseToggle,
  CardBody,
  FieldRow,
  FieldLabel,
  DayButtonGroup,
  DayButton,
  TimeRow,
  TimeBlock,
  TimePicker,
  TimeInputWrapper,
  TimeInput,
  TimeColon,
  TimeSlotList,
  TimeSlotBox,
  TimeSlotHeader,
  TimeSlotLabel,
  TimeSlotDeleteButton,
  AddTimeSlotButton,
  OnOffLabel,
  TimeDropdown,
  TimeDropdownItem,
  MonitorSection,
  MonitorLabel,
  MonitorBox,
  MonitorGrid,
  MonitorItem,
  MonitorItemLabel,
  CheckboxIconUnchecked,
  CheckboxIconChecked,
  NextDayBadge,
} from './WeeklyScheduleStyled';

// 시간 선택 옵션 (한 곳에서만 생성)
const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

// OFF 가 ON 보다 같거나 빠르면 자정을 넘겨 "다음 날" 꺼지는 슬롯
const toMinutes = time => parseInt(time?.hour || '0', 10) * 60 + parseInt(time?.minute || '0', 10);
const isOvernightSlot = slot => toMinutes(slot.offTime) <= toMinutes(slot.onTime);

// 요일 키 → 한글
const DAY_LABEL = {
  monday: '월',
  tuesday: '화',
  wednesday: '수',
  thursday: '목',
  friday: '금',
  saturday: '토',
  sunday: '일',
};
const DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

// 시 / 분 단일 셀렉터 (드롭다운 토글)
const TimeCell = ({ value, isOpen, onToggle, options, onSelect }) => (
  <TimeInputWrapper>
    <TimeInput type="button" onClick={onToggle}>
      <span>{value || '00'}</span>
      <KeyboardArrowDownIcon style={{ fontSize: 16, color: '#737373' }} />
    </TimeInput>
    {isOpen && (
      <TimeDropdown>
        {options.map(opt => (
          <TimeDropdownItem key={opt} isSelected={value === opt} onClick={() => onSelect(opt)}>
            {opt}
          </TimeDropdownItem>
        ))}
      </TimeDropdown>
    )}
  </TimeInputWrapper>
);

const WeeklyScheduleCard = ({
  schedule,
  relayData,
  availableLights,
  isCollapsed,
  openDropdown,
  setOpenDropdown,
  onDayToggle,
  onTimeChange,
  onLightToggle,
  onAddTimeSlot,
  onDeleteTimeSlot,
  onDeleteSchedule,
  onCopySchedule,
  onToggleCollapse,
}) => {
  const { t } = useTranslation();

  // 드롭다운 ID 빌더 (slotId + 필드)
  const buildDropdownId = (slotId, field) => `${slotId}-${field}`;
  const isOpen = id => openDropdown === id;
  const toggleOpen = id => setOpenDropdown(prev => (prev === id ? null : id));

  return (
    <Card>
      {/* 헤더: 요일명 + 액션 버튼들 */}
      <CardHeader>
        <CardTitleGroup>
          <CardTitle>스케줄</CardTitle>
          {/* <EditOutlinedIcon style={{ fontSize: 20, color: '#262626' }} /> */}
        </CardTitleGroup>
        <CardActions>
          <SmallButtonGroup>
            <CopyButton type="button" onClick={() => onCopySchedule(schedule.id)}>
              {t('복사')}
            </CopyButton>
            <DeleteButton type="button" onClick={() => onDeleteSchedule(schedule.id)}>
              {t('삭제')}
            </DeleteButton>
          </SmallButtonGroup>
          <CollapseToggle type="button" onClick={() => onToggleCollapse(schedule.id)}>
            <span>{isCollapsed ? t('펼치기') : t('접기')}</span>
            {isCollapsed ? (
              <KeyboardArrowDownIcon style={{ fontSize: 18 }} />
            ) : (
              <KeyboardArrowUpIcon style={{ fontSize: 18 }} />
            )}
          </CollapseToggle>
        </CardActions>
      </CardHeader>

      <Collapse in={!isCollapsed} timeout="auto" unmountOnExit>
        <CardBody>
          {/* 요일 다중 선택 — 카드 1개가 여러 요일에 동일 설정 적용 (요일 자체는 카드 간 중복 OK) */}
          <FieldRow>
            <FieldLabel>{t('요일')}</FieldLabel>
            <DayButtonGroup>
              {DAY_ORDER.map(day => (
                <DayButton
                  key={day}
                  type="button"
                  dayKey={day}
                  isSelected={schedule.days.includes(day)}
                  onClick={() => onDayToggle(schedule.id, day)}>
                  {DAY_LABEL[day]}
                </DayButton>
              ))}
            </DayButtonGroup>
          </FieldRow>

          {/* 타임슬롯 목록 (디자인은 1개만 그리지만 기존 기능상 N개 지원) */}
          <TimeSlotList>
            {schedule.timeSlots.map((slot, slotIndex) => (
              <TimeSlotBox key={slot.id}>
                {schedule.timeSlots.length > 1 && (
                  <TimeSlotHeader>
                    <TimeSlotLabel>
                      {t('스케줄')} {slotIndex + 1}
                    </TimeSlotLabel>
                    <TimeSlotDeleteButton type="button" onClick={() => onDeleteTimeSlot(schedule.id, slot.id)}>
                      {t('삭제')}
                    </TimeSlotDeleteButton>
                  </TimeSlotHeader>
                )}

                {/* ON / OFF 시간 행 */}
                <TimeRow>
                  <TimeBlock>
                    <OnOffLabel variant="on">ON</OnOffLabel>
                    <TimePicker>
                      <TimeCell
                        value={slot.onTime?.hour}
                        isOpen={isOpen(buildDropdownId(slot.id, 'onHour'))}
                        onToggle={() => toggleOpen(buildDropdownId(slot.id, 'onHour'))}
                        options={HOURS}
                        onSelect={v => onTimeChange(schedule.id, slot.id, 'onTime', 'hour', v)}
                      />
                      <TimeColon>:</TimeColon>
                      <TimeCell
                        value={slot.onTime?.minute}
                        isOpen={isOpen(buildDropdownId(slot.id, 'onMinute'))}
                        onToggle={() => toggleOpen(buildDropdownId(slot.id, 'onMinute'))}
                        options={MINUTES}
                        onSelect={v => onTimeChange(schedule.id, slot.id, 'onTime', 'minute', v)}
                      />
                    </TimePicker>
                  </TimeBlock>
                  <TimeBlock>
                    <OnOffLabel>OFF</OnOffLabel>
                    {isOvernightSlot(slot) && <NextDayBadge>{t('익일')}</NextDayBadge>}
                    <TimePicker>
                      <TimeCell
                        value={slot.offTime?.hour}
                        isOpen={isOpen(buildDropdownId(slot.id, 'offHour'))}
                        onToggle={() => toggleOpen(buildDropdownId(slot.id, 'offHour'))}
                        options={HOURS}
                        onSelect={v => onTimeChange(schedule.id, slot.id, 'offTime', 'hour', v)}
                      />
                      <TimeColon>:</TimeColon>
                      <TimeCell
                        value={slot.offTime?.minute}
                        isOpen={isOpen(buildDropdownId(slot.id, 'offMinute'))}
                        onToggle={() => toggleOpen(buildDropdownId(slot.id, 'offMinute'))}
                        options={MINUTES}
                        onSelect={v => onTimeChange(schedule.id, slot.id, 'offTime', 'minute', v)}
                      />
                    </TimePicker>
                  </TimeBlock>
                </TimeRow>

                {/* 모니터(조명) 설정 */}
                <MonitorSection>
                  <MonitorLabel>{t('모니터 설정')}</MonitorLabel>
                  <MonitorBox>
                    <MonitorGrid>
                      {availableLights.map(num => {
                        const relayInfo = relayData?.find(r => r.relayChannel === num);
                        const lightName = relayInfo?.relayName || `${t('전원')}${num}`;
                        return (
                          <MonitorItem key={num}>
                            <Checkbox
                              checked={slot.lights.includes(num)}
                              onChange={() => onLightToggle(schedule.id, slot.id, num)}
                              disableRipple
                              icon={<CheckboxIconUnchecked />}
                              checkedIcon={
                                <CheckboxIconChecked>
                                  <CheckIcon style={{ fontSize: 18, color: '#fff' }} />
                                </CheckboxIconChecked>
                              }
                              sx={{ padding: 0, '&:hover': { backgroundColor: 'transparent' } }}
                            />
                            <MonitorItemLabel>{lightName}</MonitorItemLabel>
                          </MonitorItem>
                        );
                      })}
                    </MonitorGrid>
                  </MonitorBox>
                </MonitorSection>
              </TimeSlotBox>
            ))}
          </TimeSlotList>
        </CardBody>
      </Collapse>
    </Card>
  );
};

export default WeeklyScheduleCard;
